// Administración de PLANES (Req. 25) — EXCLUSIVO del administrador global
// (system_admin). Permite:
//   - Ver cada plan con sus LÍMITES y el nº de CUENTAS que lo usan (estadística).
//   - EDITAR los límites (y la metadata) de cada plan.
//   - CREAR planes nuevos de forma dinámica.
//   - BORRAR un plan MIGRANDO sus cuentas a otro plan (se analiza cuántas hay).
//
// Las dos métricas de cuota son fijas (ver api/src/plans/planLimits.ts):
//   - catalog_records (ventana total): nº de discos de la colección.
//   - enrichment_ops  (ventana mensual): operaciones de enriquecimiento.
// Un límite VACÍO significa ILIMITADO (max_count = null en la BD).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useApi } from '../api/ApiContext.js';
import { AlertMessage, AsyncBoundary, Badge, ButtonSpinner, Card, Icon, PageHeader } from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';
import type { Plan, PlanInput, PlanLimitInput } from '../types.js';

/**
 * Métricas de cuota (fijas) con su ventana. La etiqueta legible y la ayuda se
 * resuelven por i18n a partir de la clave de métrica (plansAdmin.metricLabel/metricHelp).
 */
const METRICS: { metric: string; period: string }[] = [
  { metric: 'catalog_records', period: 'total' },
  { metric: 'enrichment_ops', period: 'monthly' },
];

/** Etiqueta legible de una métrica (i18n, con fallback a la clave). */
function metricLabel(t: TFunction, metric: string): string {
  return t(`plansAdmin.metricLabel.${metric}`, { defaultValue: metric });
}

/** Texto de ayuda de una métrica (i18n, con fallback vacío). */
function metricHelp(t: TFunction, metric: string): string {
  return t(`plansAdmin.metricHelp.${metric}`, { defaultValue: '' });
}

/** Devuelve el max_count de una métrica dentro de los límites de un plan. */
function limitValue(plan: Plan, metric: string): number | null {
  const l = plan.limits?.find((x) => x.metric === metric);
  return l ? l.maxCount : null;
}

/** Formatea un límite para mostrar: null → "Ilimitado". */
function fmtLimit(t: TFunction, value: number | null): string {
  return value === null ? t('plansAdmin.unlimited') : value.toLocaleString(undefined);
}

/** Formatea un precio en CENTAVOS a moneda (0 → "Gratis"). */
function fmtPrice(t: TFunction, cents: number, currency: string): string {
  if (!cents || cents <= 0) return t('plansAdmin.free');
  return `${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

/** Convierte un texto de precio en moneda (ej. "9.99") a centavos enteros. */
function toCents(raw: string): number {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
}

/** Centavos → texto editable en unidades monetarias (ej. 999 → "9.99"; 0 → ""). */
function centsToInput(cents: number): string {
  return cents && cents > 0 ? String(cents / 100) : '';
}

export function PlansAdminView() {
  const { t } = useTranslation();
  const api = useApi();
  const plans = useAsync(() => api.admin.listPlans(), []);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="flex flex-col">
      <PageHeader title={t('plansAdmin.pageTitle')} subtitle={t('plansAdmin.pageSubtitle')} back />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <AlertMessage message={error} />
        {message && (
          <div className="p-space-sm rounded-lg bg-goldmine-mint/15 border border-goldmine-mint/40 text-goldmine-mint font-body-sm text-body-sm">
            {message}
          </div>
        )}

        <button
          className="btn-secondary btn-sm self-start"
          type="button"
          onClick={() => setCreating((c) => !c)}
        >
          <Icon name={creating ? 'close' : 'add'} className="text-[16px]" />{' '}
          {creating ? t('plansAdmin.close') : t('plansAdmin.newPlan')}
        </button>

        {creating && (
          <PlanCreateForm
            onError={setError}
            onDone={() => {
              setCreating(false);
              setMessage(t('plansAdmin.planCreated'));
              plans.reload();
            }}
          />
        )}

        <AsyncBoundary state={plans}>
          {(data) => {
            const items = data.items;
            if (items.length === 0)
              return (
                <div className="surface-card text-center text-on-surface-variant font-body-sm text-body-sm py-space-lg">
                  {t('plansAdmin.empty')}
                </div>
              );
            const totalAccounts = items.reduce((sum, p) => sum + (p.account_count ?? 0), 0);
            return (
              <div className="flex flex-col gap-space-sm">
                <span className="font-code-catalog text-body-sm text-on-surface-variant">
                  {t('plansAdmin.summary', { count: items.length, total: totalAccounts })}
                </span>
                {items.map((p) => (
                  <PlanCard
                    key={p.id}
                    plan={p}
                    allPlans={items}
                    onError={setError}
                    onMessage={setMessage}
                    onChanged={() => plans.reload()}
                  />
                ))}
              </div>
            );
          }}
        </AsyncBoundary>
      </div>
    </div>
  );
}

/** Tarjeta de un plan: estadística, límites editables, borrado con migración. */
function PlanCard({
  plan,
  allPlans,
  onError,
  onMessage,
  onChanged,
}: {
  plan: Plan;
  allPlans: Plan[];
  onError: (m: string | null) => void;
  onMessage: (m: string | null) => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const accounts = plan.account_count ?? 0;

  async function remove() {
    // Analiza cuántas cuentas usan este plan y pide el destino para migrarlas.
    const others = allPlans.filter((p) => p.id !== plan.id);
    if (others.length === 0) {
      onError(t('plansAdmin.noTargetPlan'));
      return;
    }
    const options = others.map((p) => `${p.code} (${p.name})`).join('\n');
    const prompt1 =
      accounts > 0
        ? t('plansAdmin.deletePromptWithAccounts', { name: plan.name, count: accounts, options })
        : t('plansAdmin.deletePromptNoAccounts', { name: plan.name, options });
    const target = prompt(prompt1);
    if (target === null) return;
    const code = target.trim();
    if (!others.some((p) => p.code === code)) {
      onError(t('plansAdmin.invalidTargetCode', { code }));
      return;
    }
    setDeleting(true);
    onError(null);
    onMessage(null);
    try {
      const r = await api.admin.deletePlan(plan.id, code);
      onMessage(
        t('plansAdmin.deleteSuccess', { name: plan.name, migrated: r.migrated, target: r.migrated_to.name }),
      );
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('plansAdmin.errDelete'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Card
      title={plan.name}
      icon="workspace_premium"
      action={
        <span className="flex items-center gap-space-xs">
          {plan.is_default && <Badge tone="info">{t('plansAdmin.badgeDefault')}</Badge>}
          {!plan.active && <Badge tone="neutral">{t('plansAdmin.badgeInactive')}</Badge>}
          <Badge tone="success">{t('plansAdmin.badgeAccounts', { count: accounts })}</Badge>
        </span>
      }
    >
      <p className="font-code-catalog text-body-sm text-on-surface-variant mb-space-sm">{plan.code}</p>
      <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-sm">
        <Icon name="public" className="text-[14px] mr-space-xs align-middle" />
        {t('plansAdmin.publicNameLabel')}{' '}
        <span className="text-on-surface">{plan.public_name?.trim() || plan.name}</span>
      </p>
      {plan.description && (
        <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-sm">{plan.description}</p>
      )}

      {editing ? (
        <PlanEditForm
          plan={plan}
          onCancel={() => setEditing(false)}
          onError={onError}
          onDone={() => {
            setEditing(false);
            onMessage(t('plansAdmin.planUpdated', { name: plan.name }));
            onChanged();
          }}
        />
      ) : (
        <>
          {/* Precios mensual y anual (solo lectura). */}
          <div className="grid grid-cols-2 gap-space-xs mb-space-sm">
            <div className="surface-rack-card !p-space-sm flex flex-col gap-0.5">
              <span className="section-label text-on-surface-variant">{t('plansAdmin.monthly')}</span>
              <span className="font-code-catalog text-body-md text-primary">
                {fmtPrice(t, plan.price_cents_monthly, plan.currency)}
              </span>
            </div>
            <div className="surface-rack-card !p-space-sm flex flex-col gap-0.5">
              <span className="section-label text-on-surface-variant">{t('plansAdmin.yearly')}</span>
              <span className="font-code-catalog text-body-md text-primary">
                {fmtPrice(t, plan.price_cents_yearly, plan.currency)}
              </span>
            </div>
          </div>

          {/* Límites actuales (solo lectura hasta pulsar Editar). */}
          <div className="flex flex-col gap-space-xs mb-space-sm">
            {METRICS.map((m) => (
              <div
                key={m.metric}
                className="flex items-center justify-between gap-space-sm surface-rack-card !p-space-sm"
              >
                <span className="flex flex-col min-w-0">
                  <span className="font-body-sm text-body-sm text-on-surface">{metricLabel(t, m.metric)}</span>
                </span>
                <span className="font-code-catalog text-body-md text-primary shrink-0">
                  {fmtLimit(t, limitValue(plan, m.metric))}
                </span>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-space-xs">
            <button className="btn-secondary btn-sm" type="button" onClick={() => setEditing(true)}>
              <Icon name="edit" className="text-[16px]" /> {t('plansAdmin.editPlan')}
            </button>
            <button
              className="btn-secondary btn-sm !text-priority-urgent"
              type="button"
              onClick={() => void remove()}
              disabled={deleting || plan.is_default}
              title={plan.is_default ? t('plansAdmin.deleteDefaultTitle') : t('plansAdmin.deleteTitle')}
            >
              {deleting ? <ButtonSpinner /> : <Icon name="delete" className="text-[16px]" />}{' '}
              {t('plansAdmin.delete')}
            </button>
          </div>
        </>
      )}
    </Card>
  );
}

/** Formulario de edición de un plan existente (metadata + límites). */
function PlanEditForm({
  plan,
  onCancel,
  onDone,
  onError,
}: {
  plan: Plan;
  onCancel: () => void;
  onDone: () => void;
  onError: (m: string | null) => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [name, setName] = useState(plan.name);
  const [publicName, setPublicName] = useState(plan.public_name ?? '');
  const [description, setDescription] = useState(plan.description ?? '');
  const [priceMonthly, setPriceMonthly] = useState(centsToInput(plan.price_cents_monthly));
  const [priceYearly, setPriceYearly] = useState(centsToInput(plan.price_cents_yearly));
  const [active, setActive] = useState(plan.active);
  const [isDefault, setIsDefault] = useState(plan.is_default);
  // Límites como texto para permitir "" = ilimitado.
  const [limits, setLimits] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const m of METRICS) {
      const v = limitValue(plan, m.metric);
      init[m.metric] = v === null ? '' : String(v);
    }
    return init;
  });
  const [busy, setBusy] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    onError(null);
    try {
      const limitInputs: PlanLimitInput[] = METRICS.map((m) => {
        const raw = limits[m.metric]?.trim() ?? '';
        return { metric: m.metric, period: m.period, max_count: raw === '' ? null : Math.max(0, Math.floor(Number(raw))) };
      });
      const body: PlanInput = {
        name: name.trim(),
        public_name: publicName.trim() || null,
        description: description.trim() || null,
        price_cents_monthly: toCents(priceMonthly),
        price_cents_yearly: toCents(priceYearly),
        active,
        is_default: isDefault,
        limits: limitInputs,
      };
      await api.admin.updatePlan(plan.id, body);
      onDone();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('plansAdmin.errSave'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="flex flex-col gap-space-sm">
      <label className="field-label">{t('plansAdmin.nameLabel')}</label>
      <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} required />

      <label className="field-label">{t('plansAdmin.publicNameFieldLabel')}</label>
      <input
        className="field-input"
        value={publicName}
        onChange={(e) => setPublicName(e.target.value)}
        placeholder={t('plansAdmin.publicNamePlaceholderEdit', { name })}
      />

      <label className="field-label">{t('plansAdmin.descriptionLabel')}</label>
      <input className="field-input" value={description} onChange={(e) => setDescription(e.target.value)} />

      <div className="grid grid-cols-2 gap-space-sm">
        <div className="flex flex-col gap-space-xs">
          <label className="field-label">{t('plansAdmin.priceMonthlyLabel', { currency: plan.currency })}</label>
          <input
            className="field-input"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            placeholder={t('plansAdmin.pricePlaceholder')}
            value={priceMonthly}
            onChange={(e) => setPriceMonthly(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-space-xs">
          <label className="field-label">{t('plansAdmin.priceYearlyLabel', { currency: plan.currency })}</label>
          <input
            className="field-input"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            placeholder={t('plansAdmin.pricePlaceholder')}
            value={priceYearly}
            onChange={(e) => setPriceYearly(e.target.value)}
          />
        </div>
      </div>

      {METRICS.map((m) => (
        <div key={m.metric} className="flex flex-col gap-space-xs">
          <label className="field-label">{metricLabel(t, m.metric)}</label>
          <input
            className="field-input"
            type="number"
            min={0}
            inputMode="numeric"
            placeholder={t('plansAdmin.limitPlaceholder')}
            value={limits[m.metric] ?? ''}
            onChange={(e) => setLimits((prev) => ({ ...prev, [m.metric]: e.target.value }))}
          />
          <span className="font-body-sm text-body-sm text-on-surface-variant">
            {metricHelp(t, m.metric)} {t('plansAdmin.limitHelpSuffix')}
          </span>
        </div>
      ))}

      <label className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />{' '}
        {t('plansAdmin.activeLabel')}
      </label>
      <label className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface">
        <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />{' '}
        {t('plansAdmin.defaultLabel')}
      </label>

      <div className="flex gap-space-sm">
        <button className="btn-primary flex-1 btn-sm" type="submit" disabled={busy}>
          {busy ? t('plansAdmin.saving') : t('plansAdmin.save')}
        </button>
        <button className="btn-secondary btn-sm" type="button" onClick={onCancel} disabled={busy}>
          {t('plansAdmin.cancel')}
        </button>
      </div>
    </form>
  );
}

/** Formulario de creación de un plan nuevo. */
function PlanCreateForm({ onDone, onError }: { onDone: () => void; onError: (m: string | null) => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [publicName, setPublicName] = useState('');
  const [description, setDescription] = useState('');
  const [priceMonthly, setPriceMonthly] = useState('');
  const [priceYearly, setPriceYearly] = useState('');
  const [limits, setLimits] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const m of METRICS) init[m.metric] = '';
    return init;
  });
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    onError(null);
    try {
      const limitInputs: PlanLimitInput[] = METRICS.map((m) => {
        const raw = limits[m.metric]?.trim() ?? '';
        return { metric: m.metric, period: m.period, max_count: raw === '' ? null : Math.max(0, Math.floor(Number(raw))) };
      });
      const body: PlanInput = {
        code: code.trim().toLowerCase(),
        name: name.trim(),
        public_name: publicName.trim() || null,
        description: description.trim() || null,
        price_cents_monthly: toCents(priceMonthly),
        price_cents_yearly: toCents(priceYearly),
        limits: limitInputs,
      };
      await api.admin.createPlan(body);
      onDone();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('plansAdmin.errCreate'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('plansAdmin.newPlanCardTitle')} icon="add_circle">
      <form onSubmit={submit} className="flex flex-col gap-space-sm">
        <label className="field-label">{t('plansAdmin.codeLabel')}</label>
        <input
          className="field-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t('plansAdmin.codePlaceholder')}
          pattern="[a-z0-9_-]+"
          title={t('plansAdmin.codeTitle')}
          required
        />
        <label className="field-label">{t('plansAdmin.nameLabel')}</label>
        <input
          className="field-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('plansAdmin.namePlaceholderCreate')}
          required
        />
        <label className="field-label">{t('plansAdmin.publicNameFieldLabel')}</label>
        <input
          className="field-input"
          value={publicName}
          onChange={(e) => setPublicName(e.target.value)}
          placeholder={t('plansAdmin.publicNamePlaceholderCreate')}
        />
        <label className="field-label">{t('plansAdmin.descriptionLabel')}</label>
        <input
          className="field-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('plansAdmin.descriptionPlaceholder')}
        />

        <div className="grid grid-cols-2 gap-space-sm">
          <div className="flex flex-col gap-space-xs">
            <label className="field-label">{t('plansAdmin.priceMonthlyLabel', { currency: 'USD' })}</label>
            <input
              className="field-input"
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              placeholder={t('plansAdmin.pricePlaceholder')}
              value={priceMonthly}
              onChange={(e) => setPriceMonthly(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-space-xs">
            <label className="field-label">{t('plansAdmin.priceYearlyLabel', { currency: 'USD' })}</label>
            <input
              className="field-input"
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              placeholder={t('plansAdmin.pricePlaceholder')}
              value={priceYearly}
              onChange={(e) => setPriceYearly(e.target.value)}
            />
          </div>
        </div>

        {METRICS.map((m) => (
          <div key={m.metric} className="flex flex-col gap-space-xs">
            <label className="field-label">{metricLabel(t, m.metric)}</label>
            <input
              className="field-input"
              type="number"
              min={0}
              inputMode="numeric"
              placeholder={t('plansAdmin.limitPlaceholder')}
              value={limits[m.metric] ?? ''}
              onChange={(e) => setLimits((prev) => ({ ...prev, [m.metric]: e.target.value }))}
            />
            <span className="font-body-sm text-body-sm text-on-surface-variant">
              {metricHelp(t, m.metric)} {t('plansAdmin.limitHelpSuffix')}
            </span>
          </div>
        ))}

        <button className="btn-primary btn-sm" type="submit" disabled={busy}>
          {busy ? t('plansAdmin.creating') : t('plansAdmin.createPlan')}
        </button>
      </form>
    </Card>
  );
}
